import { useMutation, useQuery } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { AssetUserType } from '@/common/asset';
import { getRoleBadgeColor, type RoleType } from '@/common/permission';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useMutationAddAssetUser } from '@/queries/assets/users/add-asset-user';
import { getListRolesOption } from '@/queries/permissions/list-roles';
import { getListUsersOptions } from '@/queries/users/listUsers';

export type AddUserProps = {
	assetId: string;
	assetUsers?: AssetUserType[] | null;
	refetch?: () => Promise<unknown>;
	organizationId: string;
};

const groupCanCreateAssets: Record<string, boolean> = {
	Administrator: true,
	'Asset Manager': true,
};
export function AddAssetUser({ assetId, assetUsers, refetch, organizationId }: AddUserProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState('');
	const { data: listUsers } = useQuery(getListUsersOptions({ limit: 1000, offset: 0, organizationId }));
	const { data: roleListResponse } = useQuery(getListRolesOption());
	const roles = roleListResponse?.roles || [];
	const rolesById = roles.reduce(
		(acc, role) => {
			acc[role.id as string] = role;
			return acc;
		},
		{} as Record<string, RoleType>,
	);

	// Compute the list of available users to add to the asset by filtering out users that are already assigned to the asset or users that belong to groups that can create assets (since those users should be added at the group level, not the asset level)
	const availableUsers = useMemo(() => {
		if (!listUsers?.users || !rolesById || !assetUsers) {
			return [];
		}
		return listUsers?.users?.filter((user) => {
			// filter out users that are already assigned to the asset
			if (assetUsers?.find((assetUser) => assetUser.userId === user.id)) {
				return false;
			}
			// filter out users that are in a group that can create assets, since they should not be added manually to the asset
			if (user.groups.some((group) => groupCanCreateAssets[rolesById[group]?.name || ''])) {
				return false;
			}
			return true;
		});
	}, [listUsers?.users, assetUsers, rolesById]);

	const addUserMutation = useMutation(useMutationAddAssetUser());
	const addUser = async () => {
		if (!selectedUser) {
			toast.error(t('assets.users.toastSelectUserRequired'));
			return;
		}
		const user = listUsers?.users?.find((u) => u.id === selectedUser);
		if (!user) {
			toast.error(t('assets.users.toastUserNotFound'));
			return;
		}
		await addUserMutation.mutateAsync({
			assetId,
			assetPayload: {
				userId: selectedUser,
			},
		});
		refetch?.();
		setOpen(false);
		setSelectedUser('');
	};

	const onUserSelect = (value: string) => {
		const user = listUsers?.users?.find((u) => u.id === value);
		if (!user) {
			toast.error(t('assets.users.toastUserNotFound'));
			return;
		}
		setSelectedUser(value);
		if (user.groups.length === 0) {
			toast.error(t('assets.users.toastNoRoles'));
			return;
		}
	};

	// Compute the label for the selected user in the dropdown, showing their name, or a placeholder if no user is selected
	//* Since we want to display roles in the content but not in the trigger, we need to compute the label separately for the trigger and the content. The trigger will show just the user name, while the content will show the user name along with their roles.
	const selectedUserOptionLabel = useMemo(() => {
		if (!selectedUser) {
			return t('assets.users.selectUserPlaceholder');
		}
		const user = listUsers?.users?.find((u) => u.id === selectedUser);
		if (!user) {
			return t('assets.users.selectedNotFound');
		}
		return `${user.firstName} ${user.lastName}`;
	}, [selectedUser, listUsers?.users, t]);
	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button>
					<PlusIcon />
					{t('assets.users.addUser')}
				</Button>
			</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t('assets.users.addUserToAsset')}</DialogTitle>
				</DialogHeader>
				{!availableUsers?.length && (
					<div className="text-center py-12 border border-dashed rounded-lg">
						<p>{t('assets.users.noAvailableUsers')}</p>
					</div>
				)}
				{availableUsers && !!availableUsers.length && (
					<div className="py-4 space-y-4">
						<div className="space-y-2">
							<Label className="mb-2">{t('assets.users.selectUser')}</Label>
							<Select value={selectedUser} onValueChange={onUserSelect}>
								<SelectTrigger className="w-full bg-input">
									<SelectValue placeholder={t('assets.users.selectUserPlaceholder')}>
										{selectedUserOptionLabel}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									{availableUsers?.map((user) => (
										<SelectItem key={user.id} value={user.id}>
											{user.firstName} {user.lastName}{' '}
											<div className="flex flex-wrap gap-1.5">
												{user.groups.map((role) => (
													<span
														key={role}
														className={cn(
															`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded`,
															getRoleBadgeColor(rolesById[role]?.name || ''),
														)}
													>
														{rolesById[role]?.name || ''}
													</span>
												))}
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				)}
				<DialogFooter>
					<DialogClose asChild>
						<Button variant="outline">{t('common.actions.cancel')}</Button>
					</DialogClose>

					<Button onClick={addUser}>{t('assets.users.addUser')}</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
